import { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { getAllNotificationsStart, markAsReadStart } from '../../../store/redux-slices/notificationSlice';
import { selectNotificationData } from '../../../store/selectors/fetchData';
import { useLocation } from 'react-router-dom';

const ACTIVE_NOTIFICATION_ICON = '/notification_icon_active.svg';
const INACTIVE_NOTIFICATION_ICON = '/notification_icon_inactive.svg';

const useNotifications = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [notificationImage, setNotificationImage] = useState(INACTIVE_NOTIFICATION_ICON);
  const dispatch = useDispatch();
  const { notificationData, loading } = useSelector(selectNotificationData);
  const location = useLocation();

  console.log('useNotifications - isPopoverOpen:', isPopoverOpen);

  const blockScroll = useCallback(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
  }, []);

  const allowScroll = useCallback(() => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, []);

  const markAsRead = useCallback(() => {
    const unreadNotifications = getUnreadNotifications();
    if (unreadNotifications.length > 0) {
      console.log('Marking notifications as read...');
      dispatch(markAsReadStart({ markAllAsRead: true }));
    }
  }, [notificationData?.data, isPopoverOpen]);

  const fetchNotifications = useCallback(() => {
    console.log('Fetching notifications...');
    dispatch(getAllNotificationsStart());
  }, [isPopoverOpen]);

  const closeNotificationPopover = useCallback(() => {
    console.log('Closing notification popover');
    setIsPopoverOpen(false);
    allowScroll();
  }, [allowScroll]);

  const getUnreadNotifications = useCallback(() => {
    if (!notificationData?.data || !Array.isArray(notificationData.data)) {
      return [];
    }
    return notificationData.data.filter(notification =>
      !notification.isRead &&
      !notification.read &&
      notification.status !== 'read'
    );
  }, [notificationData]);

  const hasUnreadNotifications = useCallback(() => {
    const unreadNotifications = getUnreadNotifications();
    return unreadNotifications.length > 0;
  }, [getUnreadNotifications]);

  const toggleNotificationPopover = useCallback(() => {
    console.log('toggleNotificationPopover called, current state:', isPopoverOpen);
    
    setIsPopoverOpen(prevState => {
      const newState = !prevState;
      console.log('Setting new state to:', newState);

      if (newState) {
        console.log('Opening notification popover');
        fetchNotifications();
        blockScroll();
        
        setTimeout(() => {
          markAsRead();
          setNotificationImage(INACTIVE_NOTIFICATION_ICON);
        }, 500);
      } else {
        console.log('Closing notification popover');
        allowScroll();
      }

      return newState;
    });
  }, [fetchNotifications, blockScroll, allowScroll, markAsRead]);

  // Initialize
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Socket connection
  useEffect(() => {
    const socket = io('https://admin-api.owners.bet/public', {
      path: '/api/socket',
      transports: ['websocket'],
      withCredentials: false,
      autoConnect: true,
    });

    const handleNewUserSignup = () => {
      console.log('New user signup notification received');
      setNotificationImage(ACTIVE_NOTIFICATION_ICON);
      fetchNotifications();
    };

    socket.on('admin:new-user-signup', handleNewUserSignup);

    return () => {
      if (socket.connected) {
        socket.off('admin:new-user-signup', handleNewUserSignup);
        socket.disconnect();
      }
    };
  }, [fetchNotifications]);

  // Update notification icon based on unread notifications
  useEffect(() => {
    if (hasUnreadNotifications()) {
      setNotificationImage(ACTIVE_NOTIFICATION_ICON);
    } else {
      setNotificationImage(INACTIVE_NOTIFICATION_ICON);
    }
  }, [notificationData, hasUnreadNotifications]);

  // Close popup on route change
  useEffect(() => {
    if (isPopoverOpen) {
      setIsPopoverOpen(false);
      allowScroll();
    }
  }, [location.pathname, allowScroll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      allowScroll();
    };
  }, [allowScroll]);

  return {
    isPopoverOpen,
    notificationImage,
    toggleNotificationPopover,
    closeNotificationPopover,
    ACTIVE_NOTIFICATION_ICON,
    INACTIVE_NOTIFICATION_ICON,
    loading,
    notificationData,
    hasUnreadNotifications: hasUnreadNotifications(),
    unreadCount: getUnreadNotifications().length
  };
};

export default useNotifications;
